import React, { Fragment, useState, useEffect } from 'react';
import { iecWiseBrcDetailsService } from '../../../services/BrcReportService';
import { getBRCorelationService } from '../../../services/MasterService';
import { Table, Button, DatePicker, message, Input, Select } from 'antd';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getReportSummaryData, getAllAdminIECsService } from '../../../services/FileDataService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;

const BRCReport = () => {
  const location = useLocation();
  const [jsonData, setJsonData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [branchCodeNames, setBranchCodeNames] = useState(null);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(localStorage.getItem('iec_no'));
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'));
  const [isValid, setIsValid] = useState(true);
  const [billNo, setBillNo] = useState('');

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;

  // ─── TABLE STYLES ────────────────────────────────────────────────────────────
  const tableStyles = `
    /* Header cells: never wrap, always show full text — no clipping */
    .brc-table .ant-table-thead > tr > th,
    .brc-summary-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }

    /* Data cells: nowrap keeps columns aligned with headers */
    .brc-table .ant-table-tbody > tr > td,
    .brc-summary-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }

    /* Let Ant Design own the horizontal scroll — no double scrollbar */
    .brc-table .ant-table-container,
    .brc-summary-table .ant-table-container {
      overflow-x: auto !important;
    }

    .brc-table .ant-table-header,
    .brc-summary-table .ant-table-header {
      overflow: hidden !important;
    }

    .brc-table .ant-table-sticky-holder,
    .brc-summary-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  // ─── COLUMN DEFINITIONS ──────────────────────────────────────────────────────
  // Widths are set to (header char count * 9) + 32 so full text is always visible
  const columnNames = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', width: 160 , fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', width: 150 , fixed: 'left' },
    { title: 'IEC/BR CODE', dataIndex: 'iec_br', key: 'iec_br', width: 150 },
    { title: 'PORT', dataIndex: 'port_code', key: 'port_code', width: 90 },
    { title: 'FY', dataIndex: 'fy', key: 'fy', width: 80 },
    { title: 'SHB NO.', dataIndex: 'sb_no', key: 'sb_no', width: 110 },
    { title: 'SHB DT.', dataIndex: 'sb_date', key: 'sb_date', width: 110 },
    { title: 'LEO DT.', dataIndex: 'leo_date', key: 'leo_date', width: 110 },
    {
      title: 'SHB VALUE IN FCC', dataIndex: 'fcc_total', key: 'fcc_total',
      align: 'right', width: 180,
      render: (text) => formatToTwoDecimals(text),
    },
    { title: 'BRC LOT', dataIndex: 'file_category', key: 'file_category', width: 110 },
    { title: 'FCC', dataIndex: 'invoice_value_currency', key: 'invoice_value_currency', width: 80 },
    { title: 'BRC NO', dataIndex: 'brc_no', key: 'brc_no', width: 130 },
    { title: 'DATE OF REALIZATION', dataIndex: 'date_of_realization', key: 'date_of_realization', width: 190 },
    {
      title: 'VALUE', dataIndex: 'realised_value', key: 'realised_value',
      align: 'right', width: 110,
      render: (text) => formatToTwoDecimals(text),
    },
    { title: 'FCC', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: 'PORT', dataIndex: 'shb_port', key: 'shb_port', width: 90 },
    { title: 'PORT/FCC DIFF.', dataIndex: 'port_currency_difference', key: 'port_currency_difference', width: 155 },
    {
      title: 'VALUE DIFF.', dataIndex: 'value_difference', key: 'value_difference',
      align: 'right', width: 130,
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'RELEASED IN (DAYS)', dataIndex: 'payment_released_in_days', key: 'payment_released_in_days',
      align: 'right', width: 190,
      render: (text) => text != null ? text : '-',
    },
    {
      title: 'DAYS PENDING', dataIndex: 'days_pending', key: 'days_pending',
      align: 'right', width: 150,
      render: (text, record) => {
        if (text != null) return text;
        if (record.sb_date) {
          const sbDate = dayjs(record.sb_date);
          if (sbDate.isValid()) {
            return dayjs().diff(sbDate, 'day');
          }
        }
        return '-';
      }
    },
    { title: 'OBSERVATION', dataIndex: 'brc_remark', key: 'brc_remark', width: 160 },
    { title: 'EGM No', dataIndex: 'egm_no', key: 'egm_no', width: 130 },
    { title: 'EGM Dtd.', dataIndex: 'egm_date', key: 'egm_date', width: 120 },
    { title: 'EGM Remarks', dataIndex: 'egm_remarks', key: 'egm_remarks', width: 140 },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('BRCReport', columnNames);

  const dashboardReportSummaryColumns = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', width: 160 , fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', width: 150 , fixed: 'left' },
    { title: 'FY', dataIndex: 'fy', key: 'fy', width: 80 },
    { title: 'IEC/BR CODE', dataIndex: 'iec_br', key: 'iec_br', width: 150 },
    {
      title: 'NO. OF SHB', dataIndex: 'remark_count', key: 'remark_count',
      align: 'right', width: 120,
    },
    {
      title: 'FCC TOTAL', dataIndex: 'fcc_total_sum', key: 'fcc_total_sum',
      align: 'right', width: 130,
      render: (text) => formatToTwoDecimals(text),
    },
    {
      title: 'REMARK', dataIndex: 'brc_remark', key: 'brc_remark', width: 160,
      render: (text, record) => (
        <span
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => exportToExcel(text, record)}
        >
          {text}
        </span>
      ),
    },
  ];

  // ─── DATA FETCHING ────────────────────────────────────────────────────────────
  const fetchData = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes?.length ? selectedBranchCodes : null,
    };
    try {
      setIsLoading(true);
      const response = await iecWiseBrcDetailsService(selectedIEC, filters);
      setJsonData(response);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBranchCodeNames = async () => {
    try {
      const response = await getBRCorelationService(selectedIEC);
      if (response) {
        let i = 0;
        const options = response.map(item => ({
          value: item.branch_code,
          label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
          key: i++,
        }));
        setBranchCodeNames(options);
      }
    } catch (error) {
      console.error('Error fetching branch code names:', error);
    }
  };

  const getSummaryDashboard = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      bill_no: billNo.trim() || null,
      br_code: selectedBranchCodes?.length ? selectedBranchCodes : null,
    };
    try {
      setIsLoading2(true);
      const response = await getReportSummaryData('EDPMS', filters, selectedIEC);
      if (response?.data) {
        setSummaryDashboardData(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading2(false);
    }
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);
      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error('Failed to load IEC list');
    }
  };

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchData();
      getSummaryDashboard();
    }
    if (selectedIEC) {
      fetchBranchCodeNames();
    }
  }, []);

  useEffect(() => {
    if (selectedIEC) fetchBranchCodeNames();
  }, [selectedIEC]);

  // ─── HELPERS ──────────────────────────────────────────────────────────────────
  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = String(date.getFullYear()).slice(-2);
    return `${d}/${m}/${y}`;
  }

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) return new Date(dateString);
    return '';
  };

  // ─── HANDLERS ─────────────────────────────────────────────────────────────────
  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const [from_date, to_date] = value;
      if (!from_date || !to_date) { setIsValid(false); return; }
      if (to_date.isAfter(from_date.add(1, 'year'))) {
        message.error('The selected duration should not exceed 1 year.');
        setIsValid(false);
        return;
      }
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) { message.error('Please select an IEC'); return; }
    if (dates.length === 2 && isValid) {
      fetchData();
      getSummaryDashboard();
    } else {
      message.error('Please select a valid date range');
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(';');
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
    }
  };

  const handleBillSearch = (value) => setBillNo(value);
  const handleBranchCodeSelection = (selectedValues) => setSelectedBranchCodes(selectedValues);

  // ─── EXPORT ───────────────────────────────────────────────────────────────────
  const exportToExcel = async (filterRemark = null, record = null) => {
    const workbook = new ExcelJS.Workbook();
    const summaryWorksheet = workbook.addWorksheet('Summary');
    const worksheet = workbook.addWorksheet('Data');

    let data = jsonData;
    let summaryData = summaryDashboardData;

    if (record) {
      const ignoreKeys = ['remark_count', 'fcc_total_sum'];
      const isMatch = (item, searchRecord) => {
        for (let key in searchRecord) {
          if (key === 'key' || ignoreKeys.includes(key)) continue;
          if (item.hasOwnProperty(key) && item[key] !== searchRecord[key]) return false;
        }
        return true;
      };
      data = data.filter(item => isMatch(item, record));
      summaryData = summaryData.filter(item => isMatch(item, record));
    } else if (filterRemark && typeof filterRemark === 'string') {
      data = data.filter(item => item.brc_remark === filterRemark);
      summaryData = summaryData.filter(item => item.brc_remark === filterRemark);
    }

    const applyHeaderStyle = (worksheet) => {
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
        cell.alignment = { vertical: 'middle' };
      });
    };

    summaryWorksheet.columns = filteredSummaryColumns.map(col => ({
      header: col.title,
      key: col.dataIndex,
      width: 20
    }));
    applyHeaderStyle(summaryWorksheet);
    summaryData.forEach((obj) => summaryWorksheet.addRow({ ...obj }));

    worksheet.columns = filteredColumns.map(col => ({
      header: col.title,
      key: col.dataIndex,
      width: 20
    }));
    applyHeaderStyle(worksheet);
    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        brc_date: getDateOnly(obj.brc_date),
        sb_date: getDateOnly(obj.sb_date),
        leo_date: getDateOnly(obj.leo_date),
        date_of_realization: getDateOnly(obj.date_of_realization),
        egm_date: getDateOnly(obj.egm_date),
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const suffix = filterRemark && typeof filterRemark === 'string' ? `_${filterRemark}` : '';
    saveAs(blob, `Lucrative's CIP DSS - EDPMS as on ${getTodaysDate()} of ${selectedIEC}${suffix}.xlsx`);
  };

  const filteredSummaryColumns = dashboardReportSummaryColumns.filter((col) => {
    const key = String(col.dataIndex || col.key);
    const isDefinedInDetail = columnNames.some((c) => String(c.dataIndex || c.key) === key);
    return isDefinedInDetail ? visibleKeys.includes(key) : true;
  });

  // ─── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <Fragment>
      {/* Inject table-specific styles */}
      <style>{tableStyles}</style>

      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">
                  Lucrative's CIP DSS - EDPMS as on {formattedDate} of {selectedIECName}
                </h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columnNames}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button className="btn btn-primary cip-btn-submit" onClick={exportToExcel}>
                  Export to Excel
                </button>
              </div>
            </Card.Header>

            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              <ReportDateFilter dates={dates} onChange={handleDateChange} />
              <Select
                mode="multiple"
                showSearch
                placeholder="Select Branch"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleBranchCodeSelection}
                allowClear
                options={branchCodeNames}
                style={{ width: '300px' }}
              />
              <Input
                placeholder="Enter Bill No"
                allowClear
                value={billNo}
                onChange={(e) => handleBillSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Button
                type="primary"
                className="cip-btn-submit"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Card.Body>

            <Card.Body className="px-0 report-page">
              {/* ── Summary Table ── */}
              <Table
                className="brc-summary-table"
                columns={filteredSummaryColumns}
                dataSource={summaryDashboardData}
                pagination={false}
                loading={isLoading2}
                bordered
                scroll={{ x: 'max-content' }}
                
              />

              <hr />

              {/* ── Main Detail Table ── */}
              <Table
                className="brc-table"
                bordered
                columns={filteredColumns}
                dataSource={jsonData}
                loading={isLoading}
                pagination={{
                  showTotal: (total) => `Total ${total} records`,
                  showSizeChanger: true,
                }}
                scroll={{ x: 'max-content' }}
                
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default BRCReport;